import { computed, onBeforeUnmount, ref } from "vue";
import { sprintf } from "../slideshow/playlist";
import { LIFE_STORY_CONTENT, taggedUrl } from "../slideshow/share";
import { type SlideshowEvent, track } from "../slideshow/track";
import { slideshowTexts, viewer } from "./useSlideshow";

/** How long the copy button says Copied. */
const COPIED_MS = 2500;

/** For a link that isn't the slideshow's (the memory book): its own tags and tracking. */
export interface ShareLinkOptions {
  /** Instead of the slideshow's `link_copied`. */
  onCopied?: () => void;
  /** The address as it goes out through one channel; the slideshow's tags by default. */
  tag?: (url: string, medium: string) => string;
}

/**
 * The memorial page's link, as the share views hand it on: tagged with the channel it goes
 * out through, the message that goes with it, copying it, and a short note when the
 * clipboard says no. `surface` is the share button's place, for tracking.
 */
export function useShareLink(
  pageUrl: () => string,
  name: () => string,
  surface: SlideshowEvent["surface"] | (() => SlideshowEvent["surface"]),
  options: ShareLinkOptions = {}
) {
  const texts = slideshowTexts();
  const note = ref("");
  /** Just copied: the button shows it. */
  const copied = ref(false);
  let copiedTimer = 0;

  /** The page's address as it goes out through one channel; the life story's is tagged as such. */
  const urlFor = (medium: string) =>
    options.tag
      ? options.tag(pageUrl(), medium)
      : taggedUrl(
          pageUrl(),
          medium,
          viewer.kind === "life" ? LIFE_STORY_CONTENT : undefined
        );
  /** "Till minne av Birgitta – … https://…" where the message carries the link (mail, WhatsApp). */
  const textFor = (medium: string) =>
    sprintf(texts.shareText, name(), urlFor(medium));
  /** Beside a link the share sheet sends on its own: the link isn't repeated in it. */
  const linkText = computed(() => sprintf(texts.shareLinkText, name()));
  /** "En minnesfilm för Birgitta. Se fler bilder … https://…": what goes with the film. */
  const filmText = computed(() =>
    sprintf(texts.shareFilmText, name(), urlFor("film"))
  );

  /** Copy the link; `fallback` selects the visible link when the clipboard says no. */
  async function copyLink(fallback?: HTMLInputElement | null) {
    try {
      await navigator.clipboard.writeText(urlFor("copy"));
      note.value = "";
      copied.value = true;
      clearTimeout(copiedTimer);
      copiedTimer = window.setTimeout(() => {
        copied.value = false;
      }, COPIED_MS);
    } catch {
      fallback?.select();
      note.value = texts.copyFallback;
    }
    if (options.onCopied) {
      options.onCopied();
    } else {
      track({
        action: "link_copied",
        surface: typeof surface === "function" ? surface() : surface,
      });
    }
  }

  onBeforeUnmount(() => clearTimeout(copiedTimer));

  return { copied, copyLink, filmText, linkText, note, textFor, urlFor };
}
